import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTrash, faPlay, faPause, faPen } from '@fortawesome/free-solid-svg-icons';

import { TwPage } from '../../../components/TailwindPage';
import { Card } from '../../../components/Card';
import { DistributionListStatusBadge, DistributionListsStatus } from '../../../components/DistributionListStatusBadge';
import { useClassNames } from '../../../hooks/useClassNames';
import MenuContextListV2 from '../../../components/MenuContextListV2';
import { ButtonV2, CoreButtonTypes, GenericModal, useModalHelper, useToastAction } from '@metaforcelabs/metaforce-core';
import { stringToLocaleDateTimeString } from '../../../utils/date';
import { createDistributionList, getDistributionLists, deleteDistributionList, enableDistributionList, disableDistributionList } from '../../../api/distributionLists';
import { TextInput } from '../../../components/Form/TextInput';
import { useNavigate } from 'react-router-dom';

const Distribution = () => {
  const { classNames } = useClassNames();
  const [distributionLists, setDistributionLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadAction = useToastAction();
  const saveAction = useToastAction();
  const deleteAction = useToastAction();
  const enableAction = useToastAction();
  const disableAction = useToastAction();
  const createModalHelper = useModalHelper();
  const navigate = useNavigate();


  const loadData = async () => {
    await loadAction.executeAsync(async () => {
      setLoading(true);
      const lists = await getDistributionLists();
      setDistributionLists(lists);
    }, 'Error loading distribution lists');
    setLoading(false);
  };

  const handleCreateList = async (name) => {
    if (!name) {
      console.error('Distribution list name is required');
      return;
    }
    // Logic to handle creating a new distribution list
    // console.log('Creating new distribution list:', name);
    // Here you would typically call an API to create the list
    // For now, we'll just simulate it by adding to the state

    await saveAction.executeAsync(async () => {
      const created = await createDistributionList(name);
      setDistributionLists(prevLists => [...prevLists, created]);
      handleEditList(created);
    }, 'Error createing distribution list');

  }

  const handleDeleteList = async (list) => {
    await deleteAction.executeAsync(async () => {
      await deleteDistributionList(list.id);
      setDistributionLists(prevLists => prevLists.filter(l => l.id !== list.id));
    }, 'Error deleting distribution list');
  }

  const handleEnableList = async (list) => {
    await enableAction.executeAsync(async () => {
      const updatedList = await enableDistributionList(list.id);
      setDistributionLists(prevLists =>
        prevLists.map(l => l.id === list.id ? updatedList : l)
      );
    }, 'Error enabling distribution list');
  }

  const handleDisableList = async (list) => {
    await disableAction.executeAsync(async () => {
      const updatedList = await disableDistributionList(list.id);
      setDistributionLists(prevLists =>
        prevLists.map(l => l.id === list.id ? updatedList : l)
      );
    }, 'Error disabling distribution list');
  }

  useEffect(() => {

    loadData();
  }, []);



  if (loading) {
    return (
      <TwPage>
        <TwPage.Header>
          <TwPage.Header.Hdr.Fixed
            title="Distribution Lists"
            description=""
          />
        </TwPage.Header>
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="fa-spin text-2xl text-indigo-500" />
        </div>
      </TwPage>
    );
  }

  const handleEditList = (list) => {
    navigate(`/distribution/${list.id}`);
  };

  return (
    <TwPage>
      <TwPage.Header>
        <TwPage.Header.Hdr.Fixed
          title="Distribution"
          description=""
        />
      </TwPage.Header>

      <Card
        title="Distribution Lists"
        subtitle="Search and manage your distribution lists"
        removeBodyXPadding={true}
        headerActions={
          <ButtonV2 label={'New'} type={CoreButtonTypes.cta}
            onClick={() => createModalHelper.open()}
          />
        }
      >
        {/* Table */}
        <div className='relative'>
          <div className="overflow-hidden ">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
              <thead className="bg-gray-50 dark:bg-gray-600">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    NAME
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    STATUS
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    CREATED DATE
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-500">
                {distributionLists.map((list) => (
                  <tr key={list.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-150"
                          onClick={() => handleEditList(list)}
                        >
                          {list.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <DistributionListStatusBadge status={list.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {stringToLocaleDateTimeString(list.createdDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-500 dark:text-gray-400">

                      <MenuContextListV2
                        actions={[
                          {
                            name: 'Edit',
                            onClick: () => {
                              handleEditList(list);
                            },
                            disabled: false,
                            icon: ({ className }) => (
                              <FontAwesomeIcon
                                icon={faPen}
                                className={className}
                              />
                            ),
                          },
                          {
                            name: list.status === DistributionListsStatus.active ? 'Disable' : 'Enable',
                            onClick: () => list.status === DistributionListsStatus.active ? handleDisableList(list) : handleEnableList(list),
                            disabled: false,
                            icon: ({ className }) => (
                              <FontAwesomeIcon
                                icon={list.status === DistributionListsStatus.active ? faPause : faPlay}
                                className={className}
                              />
                            ),
                          },
                          {
                            name: 'Delete',
                            onClick: () => handleDeleteList(list),
                            disabled: list.status === DistributionListsStatus.active,
                            icon: ({ className }) => (
                              <FontAwesomeIcon icon={faTrash} className={className} />
                            ),
                          }
                        ]}
                        element={list}
                        deleteType={"Distribution List"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {distributionLists.length === 0 && (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No distribution lists</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new distribution list.</p>
          </div>
        )}
      </Card>
      <CreateModal modalHelper={createModalHelper} onCreate={handleCreateList} />
    </TwPage>
  );
};

export default Distribution;

const CreateModal = ({ modalHelper, onCreate }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState()


  const handleNameChange = (value) => {
    if (value && error) {
      setError(''); // Clear error if name is provided
    }
    if (!value && !error) {
      setError('Distribution list name is required'); // Set error if name is empty
    }
    setName(value);
  };

  useEffect(() => {
    // Reset name and error when modal opens or closes
    if (modalHelper.isOpen) {
      setName('');
      setError('');
    }
    // Cleanup function to reset state when modal closes
    // This ensures that the state is reset when the modal is closed

    return () => {
      setName('');
      setError('');
    }
  }, [modalHelper.isOpen]);


  return (
    <GenericModal
      open={modalHelper.isOpen}
      setOpen={open => open ? modalHelper.open() : modalHelper.close()}
      onCancel={modalHelper.close}
      confirmButtonText='Create'
      onConfirm={(() => {
        if (!name) {
          setError('Distribution list name is required');
          return;
        }
        onCreate && onCreate(name);
        modalHelper.close();
      })}
      title="Create New Distribution List"
      description="Fill in the name to create a new distribution list."
    >

      <div className='text-left my-3'>
        <TextInput
          label={"Distribution List Name"}
          placeholder={"Enter name..."}
          name={"distributionListName"}
          value={name}
          onChange={handleNameChange}
        />
        <div className="text-red-500 mt-2">
          {error && <>{error}</>}
        </div>
      </div>

    </GenericModal>
  )
}